import { useSelector } from '@/lib/store'
import {
  GET_ALL_CITY_LIST_API,
  GET_RADIOLOGY_AND_GENERAL_CITY_LIST_API,
  GET_ALL_STATE_LIST_API,
  GET_CITY_BY_STATE_ID_API,
  GET_CITY_DETAILS_BY_PINCODE_API,
  LOCATION_NOT_SERVICEABLE,
  MAP_GEOCODE,
  GEO_POSITION_SERVICABLE_API,
} from './apiConstants'
import { consumerRequest } from './request'
import useCustomToast from '@/hooks/useCustomToast'
import { isEmpty } from '@/utils/commonFunc'
import { CURRENT_SUBSIDIARY } from '@/utils/constants'
import { loadState } from '@/utils/localStorage'

const LOCATION_HELPER = () => {
  const showToast = useCustomToast()
  const loggedInMobileNumber = loadState('ctxVal') || null

  const reverseGeocoding = async (lat: number, lng: number) => {
    const url = `${MAP_GEOCODE}?latlng=${lat},${lng}`
    const response: any = await consumerRequest(url, 'post')
    if (!response?.status) {
      showToast(
        'danger',
        response?.message ||
          'Failed to fetch geocode data: Invalid response status.'
      )
      return
    }
    const data: any = response?.data
    const results = data?.results || []
    const flatMap = results?.flatMap((result: any) => result.address_components)
    const postalCodeObj = flatMap?.find((component: any) =>
      component.types.includes('postal_code')
    )
    const localityObj = flatMap?.find((component: any) =>
      component.types.includes('sublocality')
    )
    const cityObj = flatMap?.find(
      (component: any) =>
        component.types.includes('locality') ||
        component.types.includes('administrative_area_level_2')
    )
    const postal_code = postalCodeObj?.long_name || null
    const formatted_address = results?.[0]?.formatted_address || ''
    const locality = localityObj?.long_name || null
    const city = cityObj?.long_name || null

    if (!formatted_address || !postal_code) {
      showToast(
        'danger',
        'Either address or pincode not available, Please drag marker slightly.'
      )
      return
    }
    return { locality, formatted_address, postal_code, city }
  }

  const handleGetCityByPincode = async (
    pinCode: string,
    lat?: number,
    lng?: number,
    city?: string
  ) => {
    const formData = {
      pinCode: pinCode,
    }
    const apiResponse: any = await consumerRequest(
      GET_CITY_DETAILS_BY_PINCODE_API,
      'post',
      formData
    )
    if (apiResponse?.status === true) {
      return apiResponse
    } else {
      if (!isEmpty(pinCode)) {
        const payload = {
          cityName: city,
          phoneNumber: loggedInMobileNumber || '',
          pincode: pinCode,
          lat: lat,
          long: lng,
        }
        const res: any = await consumerRequest(
          LOCATION_NOT_SERVICEABLE,
          'post',
          payload
        )
      }

      return apiResponse
    }
  }

  const handleLatLongServiceable = async (
    lat: number,
    lng: number,
    city: string = ''
  ) => {
    const formData = {
      lat,
      lng,
    }

    const apiResponse: any = await consumerRequest(
      GEO_POSITION_SERVICABLE_API,
      'post',
      formData
    )

    if (apiResponse?.status === true && !isEmpty(apiResponse?.data)) {
      return apiResponse
    } else {
      if (!isEmpty(lat && lng)) {
        const reverseResponse: any = await reverseGeocoding(lat, lng)
        const postal_code = reverseResponse?.postal_code

        const payload = {
          cityName: city,
          phoneNumber: loggedInMobileNumber || '',
          pincode: postal_code,
          lat: lat,
          long: lng,
        }

        const res: any = await consumerRequest(
          LOCATION_NOT_SERVICEABLE,
          'post',
          payload
        )
      }

      return apiResponse
    }
  }

  const handleGetCityListData = async () => {
    const formData = {
      subsidiary: CURRENT_SUBSIDIARY,
    }
    const apiData: any = await consumerRequest(
      GET_ALL_CITY_LIST_API,
      'post',
      formData
    )
    const apiResponse = apiData?.data || {}
    const cityDataFromAPI = {
      popularCityList: (apiResponse?.popularCityList || [])?.filter(
        (city: any) => city.subsidiary === CURRENT_SUBSIDIARY
      ),
      nonPopularCityList: (apiResponse?.nonPopularCityList || [])?.filter(
        (city: any) => city.subsidiary === CURRENT_SUBSIDIARY
      ),
    }

    const popularCityList = Array?.isArray(cityDataFromAPI?.popularCityList)
      ? cityDataFromAPI.popularCityList
      : []
    const nonPopularCityList = Array?.isArray(
      cityDataFromAPI?.nonPopularCityList
    )
      ? cityDataFromAPI.nonPopularCityList
      : []
    const mergedCityArr = [...popularCityList, ...nonPopularCityList]
    const sortedCityArr =
      (mergedCityArr.length > 0 &&
        mergedCityArr?.sort((a, b) => a?.name?.localeCompare(b?.name))) ||
      []
    return {
      cityDataFromAPI,
      popularCityList,
      nonPopularCityList,
      mergedCityArr,
      sortedCityArr,
    }
  }

  const handleGetAllRadiologyCityListData = async (formData: any) => {
    const apiData: any = await consumerRequest(
      GET_RADIOLOGY_AND_GENERAL_CITY_LIST_API,
      'post',
      formData
    )
    if (apiData?.status) {
      const apiResponse = (apiData?.data || [])?.filter(
        (city: any) => city.subsidiary === CURRENT_SUBSIDIARY
      )
      return { ...apiData, data: apiResponse }
    }
  }

  const handleGetAllStateList = async (formData: any = {}) => {
    const apiResponse: any = await consumerRequest(
      GET_ALL_STATE_LIST_API,
      'post',
      formData
    )
    if (apiResponse?.status) {
      return apiResponse
    } else {
      console.error('failed to fetch data', apiResponse?.message)
    }
  }

  const handleGetCityDataByStateId = async (stateUuid: string) => {
    const formData = {
      stateId: stateUuid,
    }
    const apiResponse: any = await consumerRequest(
      GET_CITY_BY_STATE_ID_API,
      'post',
      formData
    )
    if (apiResponse?.status) {
      return apiResponse
    } else {
      console.error('failed to fetch data', apiResponse?.message)
    }
  }

  return {
    reverseGeocoding,
    handleGetCityByPincode,
    handleGetCityListData,
    handleGetAllRadiologyCityListData,
    handleGetAllStateList,
    handleGetCityDataByStateId,
    handleLatLongServiceable,
  }
}

export default LOCATION_HELPER
